import axios from 'axios';
import { STORE_API_URL } from './axiosInstance';

/* ===========================================================================
   WooCommerce Store API (wc/store/v1) — public, unauthenticated catalog reads.

   This is ARCH-5. The storefront previously read its own catalog through
   `wc/v3`, which is WooCommerce's *administrative* API: listing products needs
   `read_private_products` and listing categories needs `manage_product_terms`.
   Since a browser cannot hold a secret, that meant shipping a real WordPress
   login inside the public JS bundle (`free_user`) just to read a product list.

   The Store API is purpose-built for headless storefronts: no authentication,
   only customer-safe fields, and measured roughly twice as fast (521 ms vs
   998 ms). Moving catalog reads here takes the token out of the hot path
   entirely, which also makes the BUG-13 class of bug impossible — there is no
   credential left for a sign-in to overwrite.

   `adaptStoreProduct` converts a Store API product into the shape the rest of
   the app already expects, so no page needed changing. That is deliberate: the
   same approach as the custom endpoints, where keeping call signatures identical
   kept the blast radius inside the API layer.

   TWO CONVERSIONS THAT ARE NOT OPTIONAL — both verified against the live
   backend before this was written, because getting either wrong fails silently:

   1. PRICES ARE IN MINOR UNITS. `prices.price` is "38429" with
      `currency_minor_unit: 2`, meaning ₹384.29 — which matches what wc/v3
      returns as "384.29" exactly. A field-for-field copy makes every price on
      the site 100x too high.

   2. `sale_price` INVERTS IN MEANING. wc/v3 returns "" when a product is not on
      sale; the Store API returns the *current price*. SingleProduct.jsx treats
      `sale_price` as a boolean to decide whether to show a discount, so copying
      it directly would show a bogus 0% discount on every product in the store,
      with its own price struck through.

   NOT AVAILABLE HERE: `purchase_note`, which is what gates prescription-only
   products (70.4% of this catalog). It is resolved separately and
   authoritatively — see Api/prescriptions.js. Do not reintroduce a
   `purchase_note` read against an adapted product: it will be undefined, and
   undefined silently means "no prescription required".
   =========================================================================== */

const storeApi = axios.create({
    baseURL: STORE_API_URL,
    timeout: 60000,
    headers: { 'Content-Type': 'application/json' },
});

/**
 * Convert a Store API minor-unit integer string into the decimal string the UI
 * renders. `currency_minor_unit` is read from the same `prices` object rather
 * than assumed to be 2, because it is a property of the currency.
 */
const fromMinorUnits = (value, minorUnit) => {
    if (value === null || value === undefined || value === '') return '';
    const digits = Number.isFinite(Number(minorUnit)) ? Number(minorUnit) : 2;
    const amount = Number(value) / Math.pow(10, digits);
    if (!Number.isFinite(amount)) return '';
    // wc/v3 returns prices as strings and the UI interpolates them directly,
    // so match that rather than handing components a number.
    return amount.toFixed(digits);
};

/** Map the Store API's stock booleans onto wc/v3's single `stock_status`. */
const toStockStatus = (p) => {
    if (p.is_on_backorder) return 'onbackorder';
    return p.is_in_stock ? 'instock' : 'outofstock';
};

/** Store API product -> the wc/v3-shaped object the pages already consume. */
export const adaptStoreProduct = (p) => {
    if (!p || typeof p !== 'object') return p;

    const prices = p.prices || {};
    const minor = prices.currency_minor_unit;

    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        permalink: p.permalink,
        sku: p.sku,
        type: p.type,
        description: p.description,
        short_description: p.short_description,

        price: fromMinorUnits(prices.price, minor),
        regular_price: fromMinorUnits(prices.regular_price, minor),
        // Populated only when actually on sale — see conversion note 2 above.
        sale_price: p.on_sale ? fromMinorUnits(prices.sale_price, minor) : '',
        on_sale: Boolean(p.on_sale),

        images: Array.isArray(p.images) ? p.images : [],
        categories: Array.isArray(p.categories) ? p.categories : [],
        tags: Array.isArray(p.tags) ? p.tags : [],
        attributes: Array.isArray(p.attributes) ? p.attributes : [],

        stock_status: toStockStatus(p),
        // The Store API only reports a number when stock is low; it is not a
        // general-purpose quantity, so it must not be treated as one.
        stock_quantity: typeof p.low_stock_remaining === 'number' ? p.low_stock_remaining : null,

        average_rating: p.average_rating ?? '0',
        rating_count: p.review_count ?? 0,

        // Present so `.length` and spreads behave, but genuinely empty: the
        // Store API does not expose product meta.
        meta_data: [],
    };
};

const adaptMany = (list) => (Array.isArray(list) ? list.map(adaptStoreProduct) : []);

/**
 * Paged product fetch, returning the same `{ data, total, totalPages }` envelope
 * the wc/v3 helpers did. The totals come from the X-WP-Total* headers, which the
 * Store API exposes via CORS — verified, since otherwise the browser could not
 * read them and pagination would silently collapse to one page.
 */
export const fetchStoreProducts = async (params = {}) => {
    const response = await storeApi.get('/products', { params });
    return {
        data: adaptMany(response.data),
        total: parseInt(response.headers?.['x-wp-total'] || '0', 10),
        totalPages: parseInt(response.headers?.['x-wp-totalpages'] || '1', 10),
    };
};

/** A single product by numeric id. */
export const fetchStoreProductById = async (productId) => {
    const response = await storeApi.get(`/products/${productId}`);
    return adaptStoreProduct(response.data);
};

/** A single product by slug; null when nothing matches. */
export const fetchStoreProductBySlug = async (slug) => {
    const response = await storeApi.get('/products', { params: { slug } });
    const first = Array.isArray(response.data) ? response.data[0] : null;
    return first ? adaptStoreProduct(first) : null;
};

/** Product categories. The shape already matches what the UI reads. */
export const fetchStoreCategories = async (params = {}) => {
    const response = await storeApi.get('/products/categories', { params });
    return {
        data: Array.isArray(response.data) ? response.data : [],
        total: parseInt(response.headers?.['x-wp-total'] || '0', 10),
        totalPages: parseInt(response.headers?.['x-wp-totalpages'] || '1', 10),
    };
};

/* ---------------------------------------------------------------------------
   Category slug -> id.

   NOTE: `wc/store/v1/products/categories` does NOT support a `slug` parameter,
   unlike the products endpoint which does. Passing one is silently ignored and
   you get the first category on page 1 — I verified this the hard way: querying
   four different slugs returned the same id each time. A helper that passed
   `slug` straight through has been removed rather than left to mislead someone.

   The whole list is 18 categories, so fetching it once and mapping client-side
   is both correct and cheaper than guessing.

   `hide_empty: false` is REQUIRED here and is not a detail. The Store API
   omits categories with no products by default, so without it the map is
   missing `monsoon-store` and `sports-nutrition` (both 0 products today) and
   `resolveCategoryIds` reports them as UNKNOWN SLUGS. That is the wrong
   diagnosis of the right symptom: "this category does not exist" and "this
   category exists but is empty" need different answers — the first is a code
   bug, the second is a merchandising state. It is the same trap as a filter
   that matches nothing looking like an empty shelf, one level further down,
   and it is why the catalogue appeared to hold 16 categories when WooCommerce
   reports 18.

   Customer-facing category lists (Home's tiles, Categories.jsx) deliberately
   do NOT pass this — an empty category should not be offered as a shelf to
   browse. Only slug RESOLUTION needs the complete list.
   --------------------------------------------------------------------------- */

const CATEGORY_MAP_KEY = 'store_category_slug_map_v2';
const CATEGORY_MAP_TTL = 1000 * 60 * 60 * 6; // 6 hours; categories rarely change

let categoryMapPromise = null;

const readCachedCategoryMap = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(CATEGORY_MAP_KEY));
        if (!raw || typeof raw.at !== 'number') return null;
        if (Date.now() - raw.at > CATEGORY_MAP_TTL) return null;
        return raw.map || null;
    } catch {
        return null;
    }
};

/**
 * `{ slug: id }` for every product category. Cached, and de-duplicated across
 * concurrent callers so a page with several listings does not fetch it twice.
 */
export const getCategorySlugMap = async () => {
    const cached = readCachedCategoryMap();
    if (cached) return cached;

    if (categoryMapPromise) return categoryMapPromise;

    categoryMapPromise = (async () => {
        try {
            const { data } = await fetchStoreCategories({ per_page: 100, hide_empty: false });
            const map = {};
            for (const c of data) {
                if (c?.slug) map[c.slug] = c.id;
            }
            try {
                localStorage.setItem(CATEGORY_MAP_KEY, JSON.stringify({ at: Date.now(), map }));
            } catch {
                /* storage unavailable — the in-memory promise still serves this session */
            }
            return map;
        } finally {
            // Clear so a later call can retry rather than reusing a rejected promise.
            categoryMapPromise = null;
        }
    })();

    return categoryMapPromise;
};

/**
 * Resolve slugs to category ids.
 *
 * @returns {Promise<{ids: number[], missing: string[]}>} `missing` lists slugs
 *          that do not exist in the catalogue. Callers must surface that rather
 *          than treating it as "no products": a slug that matches nothing is a
 *          configuration mistake, and returning an empty grid hides it. This is
 *          exactly how `/surgical` came to render nothing for an unknown length
 *          of time — it filtered the whole catalogue on a slug that was never
 *          there, and an empty result looks identical to "out of stock".
 */
export const resolveCategoryIds = async (slugs = []) => {
    const wanted = (slugs || []).filter(Boolean);
    if (wanted.length === 0) return { ids: [], missing: [] };

    const map = await getCategorySlugMap();
    const ids = [];
    const missing = [];
    for (const slug of wanted) {
        if (map[slug] !== undefined) ids.push(map[slug]);
        else missing.push(slug);
    }
    return { ids, missing };
};

export { storeApi };
